import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Layout } from "@/components/layout/Layout";
import { SectionHeader } from "@/components/shared/SectionHeader";
import { PricingCard } from "@/components/shared/PricingCard";
import { FeatureCard } from "@/components/shared/FeatureCard";
import { ProcessStep } from "@/components/shared/ProcessStep";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { ClipboardCheck, Users, TrendingUp, Shield, Heart, ArrowRight, Sparkles } from "lucide-react";
import nhsLogo from "@/assets/nhs-logo.png";
const Home = () => {
  return <Layout>
      {/* Hero Section */}
      <section className="relative overflow-hidden" style={{
      background: 'var(--hero-gradient)'
    }}>
        <div className="container-wide section-padding">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6 animate-fade-up">
              Better partnerships.{" "}
              <span className="text-primary">Better practice.</span>{" "}
              Better lives.
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground mb-8 max-w-2xl mx-auto animate-fade-up" style={{
            animationDelay: '0.1s'
          }}>
              Evidence-informed leadership consultancy supporting NHS practices across the UK 
              to optimise governance, align values, and perform at their best.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-up" style={{
            animationDelay: '0.2s'
          }}>
              <Link to="/start-inventory">
                <Button variant="hero" size="xl">
                  Start the Inventory
                  <ArrowRight className="h-5 w-5" />
                </Button>
              </Link>
              <Link to="/services">
                <Button variant="hero-outline" size="xl">
                  View Services
                </Button>
              </Link>
            </div>
            <div className="flex flex-col items-center gap-2 mt-8 animate-fade-up" style={{
            animationDelay: '0.3s'
          }}>
              <img src={nhsLogo} alt="NHS" className="h-10" />
              <span className="text-sm font-medium text-muted-foreground">Supporting NHS General Practice</span>
            </div>
          </div>
        </div>
      </section>

      {/* Prevention First Message */}
      <section className="py-12 md:py-16 bg-primary-light">
        <div className="container-narrow text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Shield className="h-6 w-6 text-primary" />
            <span className="text-sm font-semibold text-primary uppercase tracking-wider">
              Prevention-First Approach
            </span>
          </div>
          <p className="text-lg md:text-xl text-foreground leading-relaxed max-w-3xl mx-auto">
            This work is best done <strong>before</strong> crisis, conflict, or partnership breakdown. 
            We focus on optimisation, fairness, governance clarity, motivation, and sustained performance, 
            not just firefighting when things go wrong.
          </p>
        </div>
      </section>

      {/* 3-Step Process */}
      <section className="section-padding bg-background">
        <div className="container-wide">
          <SectionHeader eyebrow="How It Works" title="Three steps to better partnership performance" description="A structured, evidence-informed approach that meets your practice where you are." />
          
          <div className="grid md:grid-cols-3 gap-8 lg:gap-12 max-w-5xl mx-auto">
            <ProcessStep number={1} title="Complete the Inventory" description="Each partner completes a confidential leadership and values assessment, providing individual insights and establishing a baseline." />
            <ProcessStep number={2} title="Receive Your Analysis" description="Aggregated partnership insights reveal alignment, gaps, and opportunities. Choose self-facilitation or expert support." />
            <ProcessStep number={3} title="Take Meaningful Action" description="Structured workshops and facilitation help translate insights into governance improvements, better communication, and sustained performance." />
          </div>

          <div className="text-center mt-12">
            <Link to="/services">
              <Button variant="subtle" size="lg">
                Learn more about our process
                <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Pricing Tiers */}
      <section className="section-padding bg-secondary/30">
        <div className="container-wide">
          <SectionHeader eyebrow="Services & Pricing" title="Choose the right level for your practice" description="From individual insights to fully facilitated partnership sessions. Transparent pricing, no hidden costs." />

          <div className="grid md:grid-cols-3 gap-6 lg:gap-8 max-w-6xl mx-auto">
            <PricingCard level="Individual" title="Leadership & Values Inventory" price="£119" priceNote="per person" description="Personal leadership insights with automated reporting." features={["Comprehensive individual assessment", "Automated personal report", "Leadership style analysis", "Values identification", "Immediate digital delivery"]} cta="Start the Inventory" ctaLink="/start-inventory" />
            <PricingCard level="Practice Bundle" title="6 Inventory Tokens" price="£600" priceNote="save £114" description="For small to medium partnerships wanting team insights." features={["6 individual assessment tokens", "Share with team members", "Each person gets unique code", "All reports emailed individually", "Tokens never expire"]} cta="Purchase Bundle" ctaLink="/purchase" highlighted badge="Most Popular" />
            <PricingCard level="Practice Plus" title="10 Inventory Tokens" price="£1,000" priceNote="save £190" description="For larger partnerships or extended leadership teams." features={["10 individual assessment tokens", "Share across your organisation", "Each person gets unique code", "All reports emailed individually", "Best value per token"]} cta="Purchase Bundle" ctaLink="/purchase" />
          </div>

          {/* Facilitated Sessions */}
          <div className="max-w-4xl mx-auto mt-16">
            <div className="text-center mb-8">
              <span className="inline-block text-sm font-medium text-primary uppercase tracking-wider mb-3">PREMIUM SERVICES</span>
              <h3 className="font-display text-2xl md:text-3xl font-semibold text-foreground mb-2">
                Facilitated Partnership Sessions
              </h3>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                Expert facilitation for practices wanting maximum impact and professional guidance.
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="card-premium">
                <h4 className="font-display text-lg font-semibold text-foreground mb-2">
                  Half-Day Facilitation
                </h4>
                <p className="text-2xl font-bold text-primary mb-2">£2,400</p>
                <p className="text-sm text-muted-foreground mb-4">3-4 hours of focused facilitation including partnership analysis, includes inventory access prior to session.</p>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li className="flex items-center gap-2">
                    <span className="h-1.5 w-1.5 rounded-full bg-primary" />
                    Aggregated partnership report
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="h-1.5 w-1.5 rounded-full bg-primary" />
                    Structured alignment exercises
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="h-1.5 w-1.5 rounded-full bg-primary" />
                    Action planning session
                  </li>
                </ul>
              </div>
              <div className="card-premium">
                <h4 className="font-display text-lg font-semibold text-foreground mb-2">
                  Full-Day Facilitation
                </h4>
                <p className="text-2xl font-bold text-primary mb-2">£3,800</p>
                <p className="text-sm text-muted-foreground mb-4">6-7 hours of comprehensive facilitation and review focusing on action planning and value aligment.</p>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li className="flex items-center gap-2">
                    <span className="h-1.5 w-1.5 rounded-full bg-primary" />
                    Everything in half-day
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="h-1.5 w-1.5 rounded-full bg-primary" />
                    Deep-dive governance review
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="h-1.5 w-1.5 rounded-full bg-primary" />
                    Extended action planning
                  </li>
                </ul>
              </div>
            </div>
            <p className="text-center text-sm text-muted-foreground mt-6">£500–£1,000 additional for sessions outside Greater London may be charged for travel and accomodation</p>
          </div>

          <div className="text-center mt-12">
            <Link to="/services">
              <Button variant="subtle" size="lg">
                View full pricing details
                <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>


      {/* Social Impact Block */}
      <section className="section-padding bg-primary text-primary-foreground">
        <div className="container-wide">
          <div className="max-w-4xl mx-auto text-center">
            <Heart className="h-12 w-12 mx-auto mb-6 opacity-90" />
            <h2 className="font-display text-3xl md:text-4xl font-semibold mb-4">
              Purpose beyond profit
            </h2>
            <p className="text-lg opacity-90 mb-6 leading-relaxed max-w-2xl mx-auto">After reasonable professional fees and operating costs, all profits from The Partnership Lab are donated to charity. The charities rotates annually, chosen by participating practices and partners.</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/about#social-impact">
                <Button variant="secondary" size="lg">
                  Our Social Impact
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="section-padding bg-background">
        <div className="container-wide">
          <SectionHeader eyebrow="Why Choose Us" title="Built for UK general practice" description="Evidence-informed tools designed specifically for the unique challenges of GP partnerships." />

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            <FeatureCard icon={ClipboardCheck} title="Evidence-Based" description="Assessments grounded in leadership research, adapted for primary care context." />
            <FeatureCard icon={Users} title="Partnership-Focused" description="Designed for the specific dynamics of GP partnerships, not generic business teams." />
            <FeatureCard icon={TrendingUp} title="Performance-Driven" description="Focus on optimisation and sustained improvement, not just crisis intervention." />
            <FeatureCard icon={Shield} title="Confidential" description="Individual responses remain private. Aggregated insights protect anonymity." />
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="section-padding bg-secondary/30">
        <div className="container-narrow">
          <SectionHeader eyebrow="FAQ" title="Frequently asked questions" />

          <Accordion type="single" collapsible className="w-full">
            <AccordionItem value="item-1" className="card-premium mb-4 border-none">
              <AccordionTrigger className="text-left font-semibold text-foreground hover:no-underline px-2">
                Who is this for?
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground px-2">
                The Partnership Lab serves GP partners, practice managers, and PCN leaders 
                across the UK. Whether you're a well-functioning partnership looking to optimise, 
                or experiencing early signs of strain, our tools provide structured insight and support.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-2" className="card-premium mb-4 border-none">
              <AccordionTrigger className="text-left font-semibold text-foreground hover:no-underline px-2">
                Is this mediation or legal advice?
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground px-2">
                No. We provide leadership consultancy and structured facilitation, not legal mediation 
                or formal dispute resolution. If your partnership requires legal support, we can recommend 
                appropriate specialists.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-3" className="card-premium mb-4 border-none">
              <AccordionTrigger className="text-left font-semibold text-foreground hover:no-underline px-2">
                How confidential are the individual assessments?
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground px-2">
                Completely. Individual responses are never shared with other partners or the practice. 
                Partnership reports contain only aggregated insights, protecting individual anonymity 
                while revealing collective patterns.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-4" className="card-premium mb-4 border-none">
              <AccordionTrigger className="text-left font-semibold text-foreground hover:no-underline px-2">
                What happens to the charity donations?
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground px-2">
                After covering fair professional fees and operating costs, all surplus profits are donated. 
                The recipient charity rotates annually, with participating practices and users voting on 
                the selection. We publish transparency reports showing impact.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-5" className="card-premium mb-4 border-none">
              <AccordionTrigger className="text-left font-semibold text-foreground hover:no-underline px-2">
                Can you work with practices outside Greater London?
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground px-2">
                Absolutely. Levels 1 and 2 are fully remote and available UK-wide. For Level 3 facilitated 
                sessions, we add £500–£1,000 for travel and accommodation costs outside Greater London.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>

      {/* Final CTA */}
      <section className="section-padding bg-background">
        <div className="container-narrow text-center">
          <Sparkles className="h-10 w-10 text-primary mx-auto mb-6" />
          <h2 className="font-display text-3xl md:text-4xl font-semibold text-foreground mb-4">
            Ready to strengthen your partnership?
          </h2>
          <p className="text-lg text-muted-foreground mb-8 max-w-xl mx-auto">
            Start with the Individual Leadership & Values Inventory, or book a discovery call 
            to discuss your practice's specific needs.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/start-inventory">
              <Button variant="hero" size="xl">
                Start the Inventory
                <ArrowRight className="h-5 w-5" />
              </Button>
            </Link>
            <Link to="/services">
              <Button variant="hero-outline" size="xl">
                View Services
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </Layout>;
};
export default Home;